/**
 * SPM埋点追踪器
 *
 * spm编码规则: a.b.c.d
 * a - 站点/模块: dms
 * b - 页面: login, dashboard, book-detail
 * c - 区域/组件: sidebar, header, content, dialog
 * d - 元素/操作: btn-login, btn-create, menu-panel
 *
 * 埋点类型:
 * 1 - pv (页面浏览)
 * 2 - click (点击事件)
 * 3 - expose (曝光)
 * 4 - custom (自定义)
 */
import { trackSpm, trackSpmBatch } from '@/api/spm'
import store from '@/store'

// 埋点类型枚举
export const SpmType = {
  PV: 1,
  CLICK: 2,
  EXPOSE: 3,
  CUSTOM: 4
}

// 批量上报缓冲区
let batchBuffer = []
let batchTimer = null
const BATCH_SIZE = 10
const BATCH_INTERVAL = 3000

/**
 * 生成会话ID
 */
function generateSessionId() {
  let sessionId = sessionStorage.getItem('spm_session_id')
  if (!sessionId) {
    sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
    sessionStorage.setItem('spm_session_id', sessionId)
  }
  return sessionId
}

// 维护上一个页面路径，用于SPA内部导航的referrer
let lastPagePath = ''

/**
 * 构建埋点数据
 */
function buildSpmData(type, spm, options = {}) {
  const parts = spm.split('.')
  const currentPagePath = window.location.pathname + window.location.search
  const data = {
    type,
    spmA: parts[0] || 'dms',
    spmB: parts[1] || '',
    spmC: parts[2] || '',
    spmD: parts[3] || '',
    pagePath: currentPagePath,
    // 优先使用SPA内部记录的上一页路径，其次使用浏览器原生referrer
    referrer: lastPagePath || document.referrer || '',
    sessionId: generateSessionId(),
  }
  // 更新上一页路径记录
  lastPagePath = currentPagePath
  if (options.bizId) data.bizId = options.bizId
  if (options.bizType) data.bizType = options.bizType
  if (options.extra) data.extra = typeof options.extra === 'string' ? options.extra : JSON.stringify(options.extra)
  return data
}

/**
 * 上报埋点（单条，立即发送）
 */
export function spmTrack(type, spm, options = {}) {
  const data = buildSpmData(type, spm, options)
  trackSpm(data).catch(() => {
    // 静默失败，不影响用户体验
  })
}

/**
 * 批量上报埋点（缓冲后统一发送）
 */
export function spmTrackBatch(type, spm, options = {}) {
  const data = buildSpmData(type, spm, options)
  batchBuffer.push(data)

  // 达到批量大小立即发送
  if (batchBuffer.length >= BATCH_SIZE) {
    flushBatch()
    return
  }

  // 延迟发送
  if (!batchTimer) {
    batchTimer = setTimeout(flushBatch, BATCH_INTERVAL)
  }
}

/**
 * 刷新缓冲区，发送所有缓冲数据
 */
function flushBatch() {
  if (batchTimer) {
    clearTimeout(batchTimer)
    batchTimer = null
  }
  if (batchBuffer.length === 0) return

  const dataToSend = [...batchBuffer]
  batchBuffer = []

  trackSpmBatch(dataToSend).catch(() => {
    // 静默失败
  })
}

/**
 * 页面浏览埋点
 */
export function spmPv(page, options = {}) {
  spmTrack(SpmType.PV, `dms.${page}`, options)
}

/**
 * 点击事件埋点
 */
export function spmClick(spm, options = {}) {
  spmTrack(SpmType.CLICK, spm, options)
}

/**
 * 曝光埋点（使用批量上报）
 */
export function spmExpose(spm, options = {}) {
  spmTrackBatch(SpmType.EXPOSE, spm, options)
}

/**
 * 自定义埋点
 */
export function spmCustom(spm, options = {}) {
  spmTrack(SpmType.CUSTOM, spm, options)
}

/**
 * Vue插件：提供全局属性和指令
 */
export const SpmPlugin = {
  install(app) {
    // 全局属性
    app.config.globalProperties.$spm = {
      track: spmTrack,
      pv: spmPv,
      click: spmClick,
      expose: spmExpose,
      custom: spmCustom,
    }

    // v-spm 指令：自动为元素添加点击埋点
    // 用法: v-spm="'dms.dashboard.sidebar.btn-create'"
    // 用法: v-spm="{ spm: 'dms.dashboard.sidebar.btn-create', bizId: 123, bizType: 'book' }"
    app.directive('spm', {
      mounted(el, binding) {
        const handleClick = () => {
          if (typeof binding.value === 'string') {
            spmClick(binding.value)
          } else if (binding.value && binding.value.spm) {
            const { spm, ...options } = binding.value
            spmClick(spm, options)
          }
        }
        el._spmClickHandler = handleClick
        el.addEventListener('click', handleClick, true)
      },
      unmounted(el) {
        if (el._spmClickHandler) {
          el.removeEventListener('click', el._spmClickHandler, true)
          delete el._spmClickHandler
        }
      }
    })

    // v-spm-expose 指令：元素曝光埋点
    app.directive('spm-expose', {
      mounted(el, binding) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                if (typeof binding.value === 'string') {
                  spmExpose(binding.value)
                } else if (binding.value && binding.value.spm) {
                  const { spm, ...options } = binding.value
                  spmExpose(spm, options)
                }
                // 曝光只上报一次
                observer.unobserve(el)
              }
            })
          },
          { threshold: 0.5 }
        )
        observer.observe(el)
        el._spmExposeObserver = observer
      },
      unmounted(el) {
        if (el._spmExposeObserver) {
          el._spmExposeObserver.disconnect()
          delete el._spmExposeObserver
        }
      }
    })
  }
}

// 页面离开时刷新缓冲区
window.addEventListener('beforeunload', () => {
  flushBatch()
})

export default {
  SpmType,
  spmTrack,
  spmTrackBatch,
  spmPv,
  spmClick,
  spmExpose,
  spmCustom,
  SpmPlugin,
}
